import { c as _c } from "react/compiler-runtime";
import { Card, CardContent, Divider, Stack, styled, Switch, Tooltip, Typography, type Theme } from '@mui/material';
import type { Condition, BackupPolicyConfigResponse } from '@repo/api-gw-sdk';
import { BackupPolicyType, BackupStatus, ConditionOperatorEnum } from '@repo/api-gw-sdk';
import { useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary } from '@/components/accordion';
import { createScopeCondition } from '@/components/backupStatus/configurationStatusScope';
import { ButtonWithIcon } from '@/components/buttons/buttonWithIcon';
import { ConfirmationDialog } from '@/components/dialogs/confirmationDialog';
import { OptionMenu } from '@/components/option-menu/option-menu';
import { backupPolicies } from '@/components/queryBuilder/properties';
import { getResourcesScopeType, type ResourcesScopeType } from '@/components/resourcesSelection/resourceSelectionDynamicViewer';
import { ResourcesSelectionViewer } from '@/components/resourcesSelection/resourcesSelectionViewer';
import { NavigationLink } from '@/components/shared/link';
import { Loader } from '@/components/shared/loader';
import { TagsElasticContainer } from '@/components/tags/tagsElasticContainer';
import { OverflowTooltip } from '@/components/tooltips/overflowTooltip';
import { AccountAsVaultTag, VaultTag } from '@/components/vaults/vaultTag';
import { useFeatureFlags } from '@/contexts/useFeatureFlags';
import { useRoles } from '@/contexts/useRoles';
import { useWorkspace } from '@/contexts/useWorkspace';
import type { BackupVault } from '@/data/vaults/backupVault';
import { createCombineCondition } from '@/types/advanceFilter';
import { pluralize } from '@/utils/string';
import { BackupPolicyConditionTitle } from './BackupPolicyConditionEditor';
import { EntraIdResourceSelectionViewer } from './EntraIdResourceSelectionViewer';
import { GwsResourceSelectionViewer } from './GwsResourceSelectionViewer';
import { M365ResourceSelectionViewer } from './M365ResourceSelectionViewer';
import { backupPolicyNativeProvider, isAzureEntraIdPolicyType, isContinuousPolicyType, isGoogleWorkspacePolicyType, isMicrosoft365PolicyType, isNativePolicyType, isPitrPolicyType, isSingleTenantSaasPolicyType } from './backupPoliciesTypes';
import { useAllBackupPolicyProperties } from './backupPolicyProperties';
import { BackupPolicyClassificationSchedule, BackupPolicyContinuousSchedulesTable, BackupPolicyScheduleTimezoneNote, BackupPolicySchedulesTable } from './backupPolicySchedulesTable';
import { BackupPolicyTypeTag } from './backupPolicyTypeTag';
import { BackupPolicyTypeDescription } from './customPolicySupportedDisclaimer';
export const BPCard = styled(Card)((props: {
  disabled: boolean;
  theme?: Theme;
  style?: object;
}) => {
  return {
    border: '1px solid var(--mui-palette-divider)',
    backgroundColor: props.disabled ? 'var(--mui-palette-background-default)' : 'var(--mui-palette-background-paper)',
    ...props.theme?.typography.body2,
    textAlign: 'left',
    color: props.theme?.palette.text.secondary,
    minWidth: '550px',
    minHeight: '170px',
    boxShadow: 'none',
    paddingTop: '12px',
    borderRadius: '16px',
    '& .MuiCardContent-root': {
      padding: '0px 24px 24px'
    },
    ...props.style
  };
});
interface BackupPolicyCardProps {
  processing?: boolean;
  policy: BackupPolicyConfigResponse;
  vaults?: BackupVault[];
  defaultExpanded?: boolean;
  onEdit?: (policy: BackupPolicyConfigResponse) => void;
  onDelete?: (policy: BackupPolicyConfigResponse) => void;
  onDuplicate?: (policy: BackupPolicyConfigResponse) => void;
  onEnabledPolicyUpdate?: (policy: BackupPolicyConfigResponse) => Promise<boolean>;
}
export function BackupPolicyCard(props) {
  const $ = _c(198);
  const enabled = props.policy.enabled;
  const {
    snackbar
  } = useWorkspace();
  const {
    isAuthorized
  } = useRoles();
  const {
    InventoryStatusColumns,
    PolicyAndVaultMetadataLabels: t0
  } = useFeatureFlags();
  const PolicyAndVaultMetadataLabels = t0 === undefined ? false : t0;
  const allBackupPolicyProperties = useAllBackupPolicyProperties();
  const [vaultsSectionExpanded, setVaultsSectionExpanded] = useState(!!props.defaultExpanded);
  const [disablePolicyDialogOpen, setDisablePolicyDialogOpen] = useState(false);
  const hasClassificationSchedule = !!props.policy.classificationSchedule;
  const schedulesLabel = hasClassificationSchedule ? "Schedules" : "Backup schedules";
  let T0;
  let T1;
  let T2;
  let T3;
  let T4;
  let T5;
  let t1;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  let togglePolicyEnabledState;
  if ($[0] !== InventoryStatusColumns || $[1] !== PolicyAndVaultMetadataLabels || $[2] !== allBackupPolicyProperties || $[3] !== enabled || $[4] !== isAuthorized || $[5] !== props || $[6] !== schedulesLabel || $[7] !== snackbar || $[8] !== vaultsSectionExpanded) {
    const matchingVaults = props.vaults?.filter(vault => props.policy?.schedules.find(schedule => schedule.vaultId === vault.id)).slice(0, 2);
    let t17;
    if ($[32] !== props.policy.condition) {
      t17 = getResourcesScopeType(props.policy.condition, "allIncluded");
      $[32] = props.policy.condition;
      $[33] = t17;
    } else {
      t17 = $[33];
    }
    const resourcesScopeType = t17;
    let t18;
    if ($[34] !== props.policy.policyType) {
      t18 = isSingleTenantSaasPolicyType(props.policy.policyType);
      $[34] = props.policy.policyType;
      $[35] = t18;
    } else {
      t18 = $[35];
    }
    const isSaasWorkspace = t18;
    let t19;
    if ($[36] !== props || $[37] !== snackbar) {
      t19 = async () => {
        const enabled_0 = props.policy.enabled;
        const updated = await props.onEnabledPolicyUpdate?.(props.policy);
        if (updated === false) {
          return;
        }
        snackbar.showMessage(<><span className="font-semibold">{props.policy.name}</span> backup policy is now {enabled_0 ? "disabled" : "enabled"}.</>);
      };
      $[36] = props;
      $[37] = snackbar;
      $[38] = t19;
    } else {
      t19 = $[38];
    }
    togglePolicyEnabledState = t19;
    let t20;
    if ($[39] !== enabled || $[40] !== togglePolicyEnabledState) {
      t20 = () => {
        if (enabled) {
          setDisablePolicyDialogOpen(true);
        } else {
          togglePolicyEnabledState();
        }
      };
      $[39] = enabled;
      $[40] = togglePolicyEnabledState;
      $[41] = t20;
    } else {
      t20 = $[41];
    }
    const onToggleClicked = t20;
    const t21 = props.policy?.id || "";
    let T6;
    let T7;
    let t22;
    let t23;
    let t24;
    let t25;
    let t26;
    let t27;
    if ($[42] !== InventoryStatusColumns || $[43] !== enabled || $[44] !== isAuthorized || $[45] !== isSaasWorkspace || $[46] !== props.onDelete || $[47] !== props.onDuplicate || $[48] !== props.onEdit || $[49] !== props.onEnabledPolicyUpdate || $[50] !== props.policy.attachedResources || $[51] !== props.policy.name || $[52] !== props.policy.policyType || $[53] !== props.processing || $[54] !== t21) {
      const inventoryFilterCondition = {
        operator: ConditionOperatorEnum.ContainsAnyOf,
        value: [t21],
        property: backupPolicies.name
      };
      T5 = BPCard;
      t15 = !enabled;
      t16 = "backup-policy-card";
      T7 = Stack;
      t25 = "row";
      if ($[66] === Symbol.for("react.memo_cache_sentinel")) {
        t26 = {
          maxWidth: "100%",
          justifyContent: "space-between",
          gap: "12px",
          alignItems: "center",
          margin: "12px 24px"
        };
        $[66] = t26;
      } else {
        t26 = $[66];
      }
      let t28;
      if ($[67] === Symbol.for("react.memo_cache_sentinel")) {
        t28 = {
          alignItems: "center",
          gap: "12px",
          minWidth: "0px",
          flexShrink: 1
        };
        $[67] = t28;
      } else {
        t28 = $[67];
      }
      let t29;
      if ($[68] === Symbol.for("react.memo_cache_sentinel")) {
        t29 = {
          minWidth: "0px",
          flexShrink: 1
        };
        $[68] = t29;
      } else {
        t29 = $[68];
      }
      let t30;
      if ($[69] !== props.policy.name) {
        t30 = <Stack sx={t29}><OverflowTooltip text={props.policy.name} renderContent={_temp} /></Stack>;
        $[69] = props.policy.name;
        $[70] = t30;
      } else {
        t30 = $[70];
      }
      let t31;
      if ($[71] !== props.policy.policyType) {
        t31 = <BackupPolicyTypeTag policyType={props.policy.policyType} />;
        $[71] = props.policy.policyType;
        $[72] = t31;
      } else {
        t31 = $[72];
      }
      if ($[73] !== t30 || $[74] !== t31) {
        t27 = <Stack direction="row" sx={t28}>{t30}{t31}</Stack>;
        $[73] = t30;
        $[74] = t31;
        $[75] = t27;
      } else {
        t27 = $[75];
      }
      T6 = Stack;
      t22 = "row";
      if ($[76] === Symbol.for("react.memo_cache_sentinel")) {
        t23 = {
          alignItems: "center",
          gap: "16px"
        };
        $[76] = t23;
      } else {
        t23 = $[76];
      }
      t24 = enabled && !isSaasWorkspace && <>{props.processing ? <Loader size="medium" /> : <NavigationLink path="/inventory" searchParams={{
          query: createCombineCondition([inventoryFilterCondition, createScopeCondition("backupPolicyResources", [BackupStatus.ExcludedFromBackup, BackupStatus.Disconnected, BackupStatus.AccessLost, BackupStatus.Unsupported, BackupStatus.ClassificationFailed, BackupStatus.LimitExceeded], InventoryStatusColumns)])
        }} disabled={!isAuthorized("read:inventory")} className="text-(--mui-palette-primary-main)"><ButtonWithIcon text={`${props.policy.attachedResources ?? 0} ${pluralize("Resource", props.policy.attachedResources ?? 0)}`} iconPosition="right" icon="material-symbols-arrow-right-alt" /></NavigationLink>}{(props.onEdit && props.onDelete && props.onDuplicate || props.onEnabledPolicyUpdate) && <Divider sx={{
          height: "16px"
        }} orientation="vertical" />}</>;
      $[42] = InventoryStatusColumns;
      $[43] = enabled;
      $[44] = isAuthorized;
      $[45] = isSaasWorkspace;
      $[46] = props.onDelete;
      $[47] = props.onDuplicate;
      $[48] = props.onEdit;
      $[49] = props.onEnabledPolicyUpdate;
      $[50] = props.policy.attachedResources;
      $[51] = props.policy.name;
      $[52] = props.policy.policyType;
      $[53] = props.processing;
      $[54] = t21;
      $[55] = T5;
      $[56] = T6;
      $[57] = T7;
      $[58] = t15;
      $[59] = t16;
      $[60] = t22;
      $[61] = t23;
      $[62] = t24;
      $[63] = t25;
      $[64] = t26;
      $[65] = t27;
    } else {
      T5 = $[55];
      T6 = $[56];
      T7 = $[57];
      t15 = $[58];
      t16 = $[59];
      t22 = $[60];
      t23 = $[61];
      t24 = $[62];
      t25 = $[63];
      t26 = $[64];
      t27 = $[65];
    }
    let t28;
    if ($[77] !== isAuthorized || $[78] !== isSaasWorkspace || $[79] !== props) {
      t28 = (isSaasWorkspace && props.onDelete || props.onEdit && props.onDelete && props.onDuplicate) && <OptionMenu options={[...(isSaasWorkspace ? [{
        id: "edit-policy",
        text: "Edit",
        menuItemProps: {
          disabled: !isAuthorized("update:policies"),
          onClick: () => {
            if (!props.policy) {
              return;
            }
            props.onEdit?.(props.policy);
          }
        }
      }, {
        id: "divider",
        divider: true
      }] : [{
        id: "edit-policy",
        text: "Edit",
        menuItemProps: {
          disabled: !isAuthorized("update:policies"),
          onClick: () => {
            if (!props.policy) {
              return;
            }
            props.onEdit?.(props.policy);
          }
        }
      }, {
        id: "duplicate-policy",
        text: "Duplicate",
        menuItemProps: {
          disabled: !isAuthorized("create:policies"),
          onClick: () => {
            props.onDuplicate?.(props.policy);
          }
        }
      }, {
        id: "divider",
        divider: true
      }]), {
        id: "delete-policy",
        text: "Delete",
        menuItemProps: {
          disabled: !isAuthorized("delete:policies"),
          onClick: () => {
            if (!props.policy) {
              return;
            }
            props.onDelete?.(props.policy);
          }
        }
      }]} />;
      $[77] = isAuthorized;
      $[78] = isSaasWorkspace;
      $[79] = props;
      $[80] = t28;
    } else {
      t28 = $[80];
    }
    let t29;
    if ($[81] !== enabled || $[82] !== isAuthorized || $[83] !== onToggleClicked || $[84] !== props.onEnabledPolicyUpdate) {
      t29 = props.onEnabledPolicyUpdate && <Tooltip title={enabled ? "This policy is enabled" : "This policy is disabled"}><Switch disabled={!isAuthorized("update:policies")} checked={enabled} onChange={onToggleClicked} /></Tooltip>;
      $[81] = enabled;
      $[82] = isAuthorized;
      $[83] = onToggleClicked;
      $[84] = props.onEnabledPolicyUpdate;
      $[85] = t29;
    } else {
      t29 = $[85];
    }
    let t30;
    if ($[86] !== T6 || $[87] !== t22 || $[88] !== t23 || $[89] !== t24 || $[90] !== t28 || $[91] !== t29) {
      t30 = <T6 direction={t22} sx={t23}>{t24}{t28}{t29}</T6>;
      $[86] = T6;
      $[87] = t22;
      $[88] = t23;
      $[89] = t24;
      $[90] = t28;
      $[91] = t29;
      $[92] = t30;
    } else {
      t30 = $[92];
    }
    if ($[93] !== T7 || $[94] !== t25 || $[95] !== t26 || $[96] !== t27 || $[97] !== t30) {
      t13 = <T7 direction={t25} sx={t26}>{t27}{t30}</T7>;
      $[93] = T7;
      $[94] = t25;
      $[95] = t26;
      $[96] = t27;
      $[97] = t30;
      $[98] = t13;
    } else {
      t13 = $[98];
    }
    if ($[99] !== PolicyAndVaultMetadataLabels || $[100] !== props.policy.tags) {
      t14 = PolicyAndVaultMetadataLabels && !!Object.keys(props.policy.tags ?? {}).length && <Stack className="px-[24px] pt-[12px]"><TagsElasticContainer tags={props.policy.tags ?? {}} expandable={true} /></Stack>;
      $[99] = PolicyAndVaultMetadataLabels;
      $[100] = props.policy.tags;
      $[101] = t14;
    } else {
      t14 = $[101];
    }
    T4 = CardContent;
    if ($[102] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = {
        marginTop: "24px",
        "&&": {
          padding: "0 0 24px"
        }
      };
      $[102] = t12;
    } else {
      t12 = $[102];
    }
    T3 = Stack;
    if ($[103] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = <Divider />;
      t10 = {
        gap: "12px"
      };
      $[103] = t10;
      $[104] = t9;
    } else {
      t10 = $[103];
      t9 = $[104];
    }
    let t31;
    if ($[105] === Symbol.for("react.memo_cache_sentinel")) {
      t31 = {
        px: "24px"
      };
      $[105] = t31;
    } else {
      t31 = $[105];
    }
    let t32;
    if ($[106] === Symbol.for("react.memo_cache_sentinel")) {
      t32 = <AccordionSummary><Typography variant="h4">{"Resource selection"}</Typography></AccordionSummary>;
      $[106] = t32;
    } else {
      t32 = $[106];
    }
    let t33;
    if ($[107] === Symbol.for("react.memo_cache_sentinel")) {
      t33 = {
        gap: "24px"
      };
      $[107] = t33;
    } else {
      t33 = $[107];
    }
    let t34;
    if ($[108] !== allBackupPolicyProperties || $[109] !== props.policy.condition || $[110] !== props.policy.exclude || $[111] !== props.policy.include || $[112] !== props.policy.policyType || $[113] !== props.policy.resourceTypes || $[114] !== resourcesScopeType) {
      t34 = isGoogleWorkspacePolicyType(props.policy.policyType) ? <GwsResourceSelectionViewer resourceId={props.policy.include?.[0]} /> : isMicrosoft365PolicyType(props.policy.policyType) ? <M365ResourceSelectionViewer resourceId={props.policy.include?.[0]} /> : isAzureEntraIdPolicyType(props.policy.policyType) ? <EntraIdResourceSelectionViewer resourceId={props.policy.include?.[0]} /> : <ResourcesSelectionViewer condition={props.policy.condition} conditionTitle={<BackupPolicyConditionTitle resourceTypes={props.policy.resourceTypes} policyType={props.policy.policyType ?? BackupPolicyType.BACKUP_POLICY_TYPE_STANDARD} />} include={props.policy.include ?? []} exclude={props.policy.exclude ?? []} resourcesScopeType={resourcesScopeType} dynamicResourcesHeader={null} allExcludedDescription={"This backup policy won\u2019t be automatically applied to any resources. Only manually included resources will be assigned to the policy."} allIncludedDescription={isPitrPolicyType(props.policy.policyType) ? "This backup policy will be automatically applied to all resources of the selected type. When new ones are discovered, they\u2019ll be included too." : "This backup policy will be automatically applied to all resources. When new ones are discovered, they\u2019ll be included too."} supportProperties={allBackupPolicyProperties.flat()} selectedResourceTypes={props.policy.resourceTypes} policyType={props.policy.policyType} />;
      $[108] = allBackupPolicyProperties;
      $[109] = props.policy.condition;
      $[110] = props.policy.exclude;
      $[111] = props.policy.include;
      $[112] = props.policy.policyType;
      $[113] = props.policy.resourceTypes;
      $[114] = resourcesScopeType;
      $[115] = t34;
    } else {
      t34 = $[115];
    }
    let t35;
    if ($[116] !== props.policy.policyType || $[117] !== props.policy.vaultStorageTier) {
      t35 = <BackupPolicyTypeDescription policyType={props.policy.policyType} showForPitr={false} vaultStorageTier={props.policy.vaultStorageTier} />;
      $[116] = props.policy.policyType;
      $[117] = props.policy.vaultStorageTier;
      $[118] = t35;
    } else {
      t35 = $[118];
    }
    let t36;
    if ($[119] !== t34 || $[120] !== t35) {
      t36 = <AccordionDetails><Stack sx={t33}>{t34}{t35}</Stack></AccordionDetails>;
      $[119] = t34;
      $[120] = t35;
      $[121] = t36;
    } else {
      t36 = $[121];
    }
    if ($[122] !== props.defaultExpanded || $[123] !== t36) {
      t11 = <Accordion defaultExpanded={props.defaultExpanded} sx={t31}>{t32}{t36}</Accordion>;
      $[122] = props.defaultExpanded;
      $[123] = t36;
      $[124] = t11;
    } else {
      t11 = $[124];
    }
    T2 = Accordion;
    t6 = vaultsSectionExpanded;
    if ($[125] !== vaultsSectionExpanded) {
      t7 = () => setVaultsSectionExpanded(!vaultsSectionExpanded);
      $[125] = vaultsSectionExpanded;
      $[126] = t7;
    } else {
      t7 = $[126];
    }
    if ($[127] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = {
        px: "24px"
      };
      $[127] = t8;
    } else {
      t8 = $[127];
    }
    T1 = AccordionSummary;
    T0 = Stack;
    t1 = "row";
    t2 = "w-full";
    if ($[128] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = {
        justifyContent: "space-between",
        alignItems: "center"
      };
      $[128] = t3;
    } else {
      t3 = $[128];
    }
    if ($[129] !== schedulesLabel) {
      t4 = <Typography variant="h4">{schedulesLabel}</Typography>;
      $[129] = schedulesLabel;
      $[130] = t4;
    } else {
      t4 = $[130];
    }
    t5 = !vaultsSectionExpanded && <div>{isNativePolicyType(props.policy.policyType) ? <AccountAsVaultTag cloudProvider={backupPolicyNativeProvider(props.policy)} region={props.policy.schedules?.[0]?.region || "Source region"} /> : !!matchingVaults?.length && matchingVaults.map(_temp2)}</div>;
    $[0] = InventoryStatusColumns;
    $[1] = PolicyAndVaultMetadataLabels;
    $[2] = allBackupPolicyProperties;
    $[3] = enabled;
    $[4] = isAuthorized;
    $[5] = props;
    $[6] = schedulesLabel;
    $[7] = snackbar;
    $[8] = vaultsSectionExpanded;
    $[9] = T0;
    $[10] = T1;
    $[11] = T2;
    $[12] = T3;
    $[13] = T4;
    $[14] = T5;
    $[15] = t1;
    $[16] = t10;
    $[17] = t11;
    $[18] = t12;
    $[19] = t13;
    $[20] = t14;
    $[21] = t15;
    $[22] = t16;
    $[23] = t2;
    $[24] = t3;
    $[25] = t4;
    $[26] = t5;
    $[27] = t6;
    $[28] = t7;
    $[29] = t8;
    $[30] = t9;
    $[31] = togglePolicyEnabledState;
  } else {
    T0 = $[9];
    T1 = $[10];
    T2 = $[11];
    T3 = $[12];
    T4 = $[13];
    T5 = $[14];
    t1 = $[15];
    t10 = $[16];
    t11 = $[17];
    t12 = $[18];
    t13 = $[19];
    t14 = $[20];
    t15 = $[21];
    t16 = $[22];
    t2 = $[23];
    t3 = $[24];
    t4 = $[25];
    t5 = $[26];
    t6 = $[27];
    t7 = $[28];
    t8 = $[29];
    t9 = $[30];
    togglePolicyEnabledState = $[31];
  }
  let t17;
  if ($[131] !== T0 || $[132] !== t1 || $[133] !== t2 || $[134] !== t3 || $[135] !== t4 || $[136] !== t5) {
    t17 = <T0 direction={t1} className={t2} sx={t3}>{t4}{t5}</T0>;
    $[131] = T0;
    $[132] = t1;
    $[133] = t2;
    $[134] = t3;
    $[135] = t4;
    $[136] = t5;
    $[137] = t17;
  } else {
    t17 = $[137];
  }
  let t18;
  if ($[138] !== T1 || $[139] !== t17) {
    t18 = <T1>{t17}</T1>;
    $[138] = T1;
    $[139] = t17;
    $[140] = t18;
  } else {
    t18 = $[140];
  }
  let t19;
  if ($[141] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = {
      paddingRight: 0,
      paddingTop: "24px"
    };
    $[141] = t19;
  } else {
    t19 = $[141];
  }
  let t20;
  if ($[142] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = {
      gap: "24px"
    };
    $[142] = t20;
  } else {
    t20 = $[142];
  }
  let t21;
  if ($[143] !== props.policy || $[144] !== schedulesLabel) {
    t21 = <BackupPolicyScheduleTimezoneNote policy={props.policy} label={schedulesLabel} />;
    $[143] = props.policy;
    $[144] = schedulesLabel;
    $[145] = t21;
  } else {
    t21 = $[145];
  }
  let t22;
  if ($[146] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = {
      gap: "24px"
    };
    $[146] = t22;
  } else {
    t22 = $[146];
  }
  let t23;
  if ($[147] !== hasClassificationSchedule) {
    t23 = hasClassificationSchedule && <Typography variant="body1">Backup schedules</Typography>;
    $[147] = hasClassificationSchedule;
    $[148] = t23;
  } else {
    t23 = $[148];
  }
  let t24;
  if ($[149] !== props.policy || $[150] !== props.vaults) {
    t24 = isContinuousPolicyType(props.policy.policyType) ? <BackupPolicyContinuousSchedulesTable policy={props.policy} /> : <BackupPolicySchedulesTable policy={props.policy} vaults={props.vaults} />;
    $[149] = props.policy;
    $[150] = props.vaults;
    $[151] = t24;
  } else {
    t24 = $[151];
  }
  let t25;
  if ($[152] !== t23 || $[153] !== t24) {
    t25 = <Stack sx={t22}>{t23}{t24}</Stack>;
    $[152] = t23;
    $[153] = t24;
    $[154] = t25;
  } else {
    t25 = $[154];
  }
  let t26;
  if ($[155] !== hasClassificationSchedule || $[156] !== props.policy) {
    t26 = hasClassificationSchedule && <BackupPolicyClassificationSchedule policy={props.policy} />;
    $[155] = hasClassificationSchedule;
    $[156] = props.policy;
    $[157] = t26;
  } else {
    t26 = $[157];
  }
  let t27;
  if ($[158] !== t21 || $[159] !== t25 || $[160] !== t26) {
    t27 = <AccordionDetails sx={t19}><Stack sx={t20}>{t21}{t25}{t26}</Stack></AccordionDetails>;
    $[158] = t21;
    $[159] = t25;
    $[160] = t26;
    $[161] = t27;
  } else {
    t27 = $[161];
  }
  let t28;
  if ($[162] !== T2 || $[163] !== t18 || $[164] !== t27 || $[165] !== t6 || $[166] !== t7 || $[167] !== t8) {
    t28 = <T2 expanded={t6} onChange={t7} sx={t8}>{t18}{t27}</T2>;
    $[162] = T2;
    $[163] = t18;
    $[164] = t27;
    $[165] = t6;
    $[166] = t7;
    $[167] = t8;
    $[168] = t28;
  } else {
    t28 = $[168];
  }
  let t29;
  if ($[169] !== T3 || $[170] !== t10 || $[171] !== t11 || $[172] !== t28 || $[173] !== t9) {
    t29 = <T3 divider={t9} sx={t10}>{t11}{t28}</T3>;
    $[169] = T3;
    $[170] = t10;
    $[171] = t11;
    $[172] = t28;
    $[173] = t9;
    $[174] = t29;
  } else {
    t29 = $[174];
  }
  let t30;
  if ($[175] !== T4 || $[176] !== t12 || $[177] !== t29) {
    t30 = <T4 sx={t12}>{t29}</T4>;
    $[175] = T4;
    $[176] = t12;
    $[177] = t29;
    $[178] = t30;
  } else {
    t30 = $[178];
  }
  let t31;
  if ($[179] !== props.policy.name) {
    t31 = <>The <span className="font-semibold">{props.policy.name}</span>{" "}backup policy will stop being applied to resources. Existing snapshots will be kept until their retention expires.</>;
    $[179] = props.policy.name;
    $[180] = t31;
  } else {
    t31 = $[180];
  }
  let t32;
  if ($[181] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = () => setDisablePolicyDialogOpen(false);
    $[181] = t32;
  } else {
    t32 = $[181];
  }
  let t33;
  if ($[182] !== togglePolicyEnabledState) {
    t33 = async () => {
      await togglePolicyEnabledState();
      setDisablePolicyDialogOpen(false);
    };
    $[182] = togglePolicyEnabledState;
    $[183] = t33;
  } else {
    t33 = $[183];
  }
  let t34;
  if ($[184] !== disablePolicyDialogOpen || $[185] !== t31 || $[186] !== t33) {
    t34 = <ConfirmationDialog isOpen={disablePolicyDialogOpen} title="Disable backup policy" description={t31} onCancel={t32} onConfirm={t33} buttonTitle="Disable Policy" />;
    $[184] = disablePolicyDialogOpen;
    $[185] = t31;
    $[186] = t33;
    $[187] = t34;
  } else {
    t34 = $[187];
  }
  let t35;
  if ($[188] !== t13 || $[189] !== t14 || $[190] !== t30 || $[191] !== t34) {
    t35 = <>{t13}{t14}{t30}{t34}</>;
    $[188] = t13;
    $[189] = t14;
    $[190] = t30;
    $[191] = t34;
    $[192] = t35;
  } else {
    t35 = $[192];
  }
  let t36;
  if ($[193] !== T5 || $[194] !== t15 || $[195] !== t16 || $[196] !== t35) {
    t36 = <T5 disabled={t15} data-testid={t16}>{t35}</T5>;
    $[193] = T5;
    $[194] = t15;
    $[195] = t16;
    $[196] = t35;
    $[197] = t36;
  } else {
    t36 = $[197];
  }
  return t36;
}
function _temp2(vault_0) {
  return <VaultTag key={vault_0.id} vault={vault_0} />;
}
function _temp(text, ref) {
  return <Typography noWrap={true} variant="h3" ref={ref}>{text}</Typography>;
}